import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChevronDown, Menu, X } from 'lucide-react'
import { NAV_LINKS, PRODUCT_LINKS, APP_REGISTER_URL } from '../constants/nav'

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
  }, [location])

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      className={`sticky top-0 z-50 transition-colors duration-200 ${
        scrolled ? 'bg-navy/95 backdrop-blur-sm shadow-lg' : 'bg-navy'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="SiteDeck Pro" className="w-8 h-8 rounded" />
            <span className="text-white font-heading font-semibold text-lg">SiteDeck</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <div className="relative group">
              <button className="flex items-center gap-1 text-white/80 hover:text-white text-sm font-medium transition-colors py-5">
                Products
                <ChevronDown className="w-4 h-4" />
              </button>
              <div className="absolute left-0 top-full hidden group-hover:block group-focus-within:block w-[360px] pt-2">
                <div className="bg-white rounded-lg border border-gray-100 shadow-xl p-2">
                  {PRODUCT_LINKS.map((link) => (
                    <Link
                      key={link.label}
                      to={link.href}
                      className="block rounded-md px-4 py-3 hover:bg-off-white transition-colors"
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span className="text-navy font-heading font-semibold">{link.label}</span>
                        <span className="text-[11px] uppercase tracking-wide text-field-orange font-semibold">
                          {link.status}
                        </span>
                      </span>
                      <span className="block text-slate text-xs leading-relaxed mt-1">
                        {link.description}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="text-white/80 hover:text-white text-sm font-medium transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <a
              href={APP_REGISTER_URL}
              className="btn-primary text-sm py-2 px-4"
            >
              Start free
            </a>
          </div>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden pb-4 space-y-3"
          >
            {NAV_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="block text-white/80 hover:text-white text-sm font-medium py-2"
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-white/10 pt-3 mt-3">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Products</p>
              {PRODUCT_LINKS.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className="block text-white/80 hover:text-white text-sm font-medium py-2"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <a href={APP_REGISTER_URL} className="btn-primary text-sm py-2 px-4 w-full text-center">
              Start free
            </a>
          </motion.div>
        )}
      </div>
    </motion.nav>
  )
}
